import React, { useCallback, useState, useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import { Bold, Italic, Heading2, Heading3, List, ListOrdered, Quote, Link2, Image as ImageIcon, Minus, Undo2, Redo2, Eraser } from "lucide-react";
import { MediaPickerDialog } from "@/components/admin/MediaPickerDialog";
import { mediaUrl } from "@/lib/api";

const Btn = ({ onClick, active, disabled, title, testid, children }) => (
  <button
    type="button"
    data-testid={testid}
    title={title}
    aria-label={title}
    disabled={disabled}
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    className="inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors disabled:opacity-40"
    style={{ color: active ? "var(--accent-fg)" : "var(--muted)", background: active ? "rgba(140,106,61,0.20)" : "transparent" }}
  >
    {children}
  </button>
);

const Divider = () => <span className="mx-1 h-5 w-px" style={{ background: "var(--card-border)" }} />;

// A clean, structured WYSIWYG editor. It controls content STRUCTURE only;
// all styling is inherited from the Acharya Holdings editorial design system.
export const RichTextEditor = ({ value, onChange }) => {
  const [pickerOpen, setPickerOpen] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { rel: "noopener noreferrer" } }),
      Image.configure({ inline: false, HTMLAttributes: { class: "article-img" } }),
      Placeholder.configure({ placeholder: "Write the article\u2026" }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: { attributes: { class: "tiptap article-content", "data-testid": "news-editor-content" } },
  });

  // Sync external value (e.g. after loading an existing article) into the editor.
  useEffect(() => {
    if (editor && value != null && value !== editor.getHTML()) {
      editor.commands.setContent(value, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, value]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes("link").href;
    const url = window.prompt("Link URL", prev || "https://");
    if (url === null) return;
    if (url.trim() === "") { editor.chain().focus().extendMarkRange("link").unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url.trim() }).run();
  }, [editor]);

  const onPickImage = (id) => {
    if (!id || !editor) return;
    const url = mediaUrl(id);
    const alt = window.prompt("Image alt text (for accessibility & SEO)", "") || "";
    editor.chain().focus().setImage({ src: url, alt }).run();
  };

  if (!editor) return null;

  return (
    <div className="rounded-[10px] border" style={{ borderColor: "var(--card-border)", background: "var(--surface)" }}>
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-0.5 rounded-t-[10px] border-b p-1.5" style={{ borderColor: "var(--card-border)", background: "var(--surface)" }}>
        <Btn testid="editor-bold" title="Bold" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}><Bold size={15} /></Btn>
        <Btn testid="editor-italic" title="Italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic size={15} /></Btn>
        <Divider />
        <Btn testid="editor-h2" title="Heading 2" active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 size={16} /></Btn>
        <Btn testid="editor-h3" title="Heading 3" active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}><Heading3 size={16} /></Btn>
        <Divider />
        <Btn testid="editor-bullet" title="Bulleted list" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}><List size={16} /></Btn>
        <Btn testid="editor-ordered" title="Numbered list" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered size={16} /></Btn>
        <Btn testid="editor-quote" title="Blockquote" active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()}><Quote size={15} /></Btn>
        <Divider />
        <Btn testid="editor-link" title="Add link" active={editor.isActive("link")} onClick={setLink}><Link2 size={15} /></Btn>
        <Btn testid="editor-image" title="Insert image" onClick={() => setPickerOpen(true)}><ImageIcon size={15} /></Btn>
        <Btn testid="editor-hr" title="Horizontal separator" onClick={() => editor.chain().focus().setHorizontalRule().run()}><Minus size={16} /></Btn>
        <Divider />
        <Btn testid="editor-undo" title="Undo" disabled={!editor.can().undo()} onClick={() => editor.chain().focus().undo().run()}><Undo2 size={15} /></Btn>
        <Btn testid="editor-redo" title="Redo" disabled={!editor.can().redo()} onClick={() => editor.chain().focus().redo().run()}><Redo2 size={15} /></Btn>
        <Btn testid="editor-clear" title="Clear formatting" onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}><Eraser size={15} /></Btn>
      </div>

      <div className="max-h-[60vh] overflow-y-auto">
        <EditorContent editor={editor} />
      </div>

      <MediaPickerDialog open={pickerOpen} onOpenChange={setPickerOpen} onSelect={onPickImage} />
    </div>
  );
};
